/**
 * Resolve the current tenant slug for this request.
 *
 * Order of precedence (post-fix):
 *   1. Explicit `?t=` query param            (path-based tenant link override)
 *   2. localStorage `fd_tenant_slug`         (sticky tenant context — set by
 *                                             TenantRedirect when the user
 *                                             entered through /t/<slug>/...)
 *   3. Subdomain of the current host         (raw subdomain visit, no path
 *                                             override yet — sets localStorage)
 *   4. DEFAULT_TENANT_SLUG                   (env-configurable, default
 *                                             `dairyfresh` for the SaaS host)
 *
 * **Why localStorage > subdomain?** The platform's master host happens to be
 * `dairyfresh.localappstore.in`, which collides with the first tenant's
 * subdomain. Without this ordering, a PureDoodh native app — whose WebView
 * loads `https://dairyfresh.localappstore.in/t/puredoodh/...` — would get
 * "puredoodh" from TenantRedirect into localStorage, then later (e.g. when
 * navigating to /app/home or /delivery/login) the subdomain extractor
 * would overwrite that with "dairyfresh", silently switching the tenant.
 *
 * **Self-hosted deployments** (custom domain like `milk.acmecorp.in`)
 * should set:
 *   REACT_APP_DEFAULT_TENANT_SLUG  — the tenant to serve when no subdomain
 *                                    or `?t=` is present (e.g. `acme`)
 *   REACT_APP_TENANT_HOST_ROOTS    — comma-separated list of apex domains
 *                                    whose subdomain IS the tenant slug
 *                                    (e.g. `acmecorp.in,acmecorp.com`)
 *   REACT_APP_PLATFORM_HOSTS       — comma-separated hosts that should NOT
 *                                    have their subdomain treated as a tenant
 *                                    slug (e.g. `www.acmecorp.in,acmecorp.in`)
 * Without these, subdomain inference is skipped for custom domains and the
 * app falls back to the default slug — much friendlier than the previous
 * behaviour of silently sending an unknown slug to the API.
 */
const _split = (s) => String(s || "").split(",").map((x) => x.trim().toLowerCase()).filter(Boolean);

// Baked-in defaults for the SaaS host — extended (not replaced) by env vars.
const BUILTIN_PLATFORM_HOSTS = [
  "localappstore.in",
  "www.localappstore.in",
  "freshdoodh.com",
  "www.freshdoodh.com",
  "localhost",
];
const BUILTIN_TENANT_HOST_ROOTS = ["localappstore.in", "freshdoodh.com"];

export const DEFAULT_TENANT_SLUG =
  (process.env.REACT_APP_DEFAULT_TENANT_SLUG || "dairyfresh").toLowerCase();

const PLATFORM_HOSTS = new Set([
  ...BUILTIN_PLATFORM_HOSTS,
  ..._split(process.env.REACT_APP_PLATFORM_HOSTS),
]);

const TENANT_HOST_ROOTS = new Set([
  ...BUILTIN_TENANT_HOST_ROOTS,
  ..._split(process.env.REACT_APP_TENANT_HOST_ROOTS),
]);

function fromSubdomain() {
  if (typeof window === "undefined") return null;
  const host = window.location.hostname;
  if (PLATFORM_HOSTS.has(host)) return null;
  const parts = host.split(".");
  // Only treat the first label as a slug if the host looks like a real
  // tenant subdomain — the last-two labels must match a configured
  // tenant-host root. Random subdomains (e.g. Emergent preview URLs, or
  // a self-hosted deploy with no configured root) don't get their
  // subdomain silently promoted to a tenant slug.
  if (parts.length >= 3) {
    const root = parts.slice(-2).join(".").toLowerCase();
    if (TENANT_HOST_ROOTS.has(root)) {
      return parts[0].toLowerCase();
    }
  }
  return null;
}

/**
 * The tenant this app is running for, read from the app's OWN context —
 * the same sources, in the same order, as getTenantSlug(): `?t=` in the
 * URL, the `/t/<slug>/` path the native build bakes into its start URL,
 * the slug TenantRedirect stored, or a tenant subdomain. Returns null when
 * none of them says, and never substitutes a default store.
 *
 * A login that must not land in another store's context (the Rider App —
 * a rider exists in exactly one store) uses this and treats null as an
 * app-configuration error instead of silently picking the default tenant.
 */
export function resolveTenantSlug() {
  // 1. Explicit ?t= override always wins (deep links, multi-tenant tooling).
  try {
    const qs = new URLSearchParams(window.location.search);
    const fromQuery = qs.get("t");
    if (fromQuery) {
      localStorage.setItem("fd_tenant_slug", fromQuery);
      return fromQuery;
    }
  } catch { /* SSR / private mode */ }
  // 2. Path-based tenant entry — `/t/<slug>/…`. This MUST beat both
  //    localStorage and subdomain inference, otherwise the app boots
  //    up with the wrong tenant on the FIRST render (before
  //    TenantRedirect's useEffect gets a chance to run and rewrite
  //    the URL to `?t=<slug>`). The exact symptom this fixes: a
  //    native Capacitor app whose baked `server.url` is
  //    `https://dairyfresh.localappstore.in/t/milk-on-wheels/`
  //    used to load DairyFresh branding + categories because
  //    AuthProvider.useEffect ran BEFORE TenantRedirect.
  try {
    const path = window.location.pathname || "";
    const m = path.match(/^\/t\/([^/]+)(?:\/|$)/);
    if (m && m[1]) {
      const pathSlug = m[1].toLowerCase();
      try { localStorage.setItem("fd_tenant_slug", pathSlug); } catch { /* SSR / private mode */ }
      return pathSlug;
    }
  } catch { /* SSR / private mode */ }
  // 3. Sticky localStorage value (set by TenantRedirect or previous resolution).
  //    Must beat subdomain inference because the platform host collides with
  //    the default tenant's subdomain.
  try {
    const stored = localStorage.getItem("fd_tenant_slug");
    if (stored) return stored;
  } catch { /* SSR / private mode */ }
  // 4. Subdomain inference (only when no sticky context exists).
  const sub = fromSubdomain();
  if (sub) {
    try { localStorage.setItem("fd_tenant_slug", sub); } catch { /* SSR / private mode */ }
    return sub;
  }
  // No tenant context.
  return null;
}

/** resolveTenantSlug(), falling back to the platform default tenant for
 *  surfaces that are allowed to show it (storefront, branding). */
export function getTenantSlug() {
  return resolveTenantSlug() || DEFAULT_TENANT_SLUG;
}

export function setTenantSlug(slug) {
  try { localStorage.setItem("fd_tenant_slug", slug); } catch { /* SSR / private mode */ }
}

/** Clear the cached tenant slug — call on logout if the next user may belong
 *  to a different tenant (e.g. a shared device on the web). Native apps don't
 *  need this since each APK is single-tenant. */
export function clearTenantSlug() {
  try { localStorage.removeItem("fd_tenant_slug"); } catch { /* SSR / private mode */ }
}
